import { Autocomplete, CircularProgress, Divider, Paper, Skeleton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { INode } from '../../definitions/Nodes'
import { IExecutionEnvironment, ISimpleExecutionEnvironment } from '../../definitions/NodesExecution'
import JSONDiff from '../editor/JSONDiff'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import NodesIcon from './nodes/NodesIcon'

interface P {
    selectedNode: INode
    execEnv: IExecutionEnvironment
    comparedExecEnv: ISimpleExecutionEnvironment
}

type SelectedTab = 'outputs' | 'inputs' | 'config'

const NodeComparePanel = ({ selectedNode, execEnv, comparedExecEnv }: P) => {
    const tabs = ['outputs', 'inputs', 'config']
    const [selectedTab, setSelectedTab] = useState<SelectedTab>('outputs')
    const [selectedField, setSelectedField] = useState<{ inputSelected: string, outputSelected: string }>({ inputSelected: '', outputSelected: '' })

    const { status: statusCompareData, response: compareData } = useQuery({
        queryKey: buildQueryKey(`node-compare-data`, selectedNode.uuid, execEnv.uuid, comparedExecEnv.uuid),
        queryFn: async () => await FlowApiClient.getNodeCompareData(selectedNode.uuid, execEnv.uuid, comparedExecEnv.uuid),
        cache: true
    })

    useEffect(() => {
        if (statusCompareData === 'success') {
            setSelectedField({
                inputSelected: compareData.inputs?.length > 0 ? compareData.inputs[0].id : '',
                outputSelected: compareData.outputs?.length > 0 ? compareData.outputs[0].id : ''
            })
        }
    }, [statusCompareData, compareData])

    const inputOptions = useMemo(() => {
        if (statusCompareData !== 'success') return []

        return compareData.inputs.map((item: any) => ({
            id: item.id,
            nodeName: item.nodeName,
            key: item.key
        }))
    }, [statusCompareData, compareData])

    const outputOptions = useMemo(() => {
        if (statusCompareData !== 'success') return []

        return compareData.outputs.map((item: any) => ({
            id: item.id,
            nodeName: item.nodeName,
            key: item.key
        }))
    }, [statusCompareData, compareData])

    const renderContent = () => {
        switch (selectedTab) {
            case 'outputs': {
                if (statusCompareData === 'loading') return <Skeleton width='100%' height='100%' />
                const selectedOutput = compareData?.outputs.find(data => data.id === selectedField?.outputSelected)
                return (
                    <JSONDiff
                        currentValue={selectedOutput ? selectedOutput.value.memoryData.data : ''}
                        comparedValue={selectedOutput ? selectedOutput.value.comparedData : ''}
                    />
                )
            }

            case 'inputs': {
                if (statusCompareData === 'loading') return <Skeleton width='100%' height='100%' />
                const selectedInput = compareData?.inputs.find(data => data.id === selectedField?.inputSelected)
                return (
                    <JSONDiff
                        currentValue={selectedInput ? selectedInput.value.memoryData.data : ''}
                        comparedValue={selectedInput ? selectedInput.value.comparedData : ''}
                    />
                )
            }

            case 'config': {
                if (statusCompareData === 'loading') return <Skeleton width='100%' height='100%' />
                return (
                    <JSONDiff
                        currentValue={compareData?.configs.memoryData.data}
                        comparedValue={compareData?.configs.comparedData}
                    />
                )
            }
        }
    }

    return (
        <Paper sx={{ width: '100%', height: '100%', padding: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <Stack sx={{ height: '100%', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                    <NodesIcon nodeType={selectedNode.type} size={32} />
                </Stack>
                <Typography
                    variant='body2'
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                    {selectedNode.name}
                </Typography>
            </Stack>
            <Divider />
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Tabs
                    value={selectedTab}
                    onChange={(_, newTab) => {
                        setSelectedTab(newTab)
                    }}>
                    {tabs.map(t => (
                        <Tab key={t} label={t} value={t} />
                    ))}
                </Tabs>
                {selectedTab === 'outputs' && (
                    <Autocomplete
                        id='outputs'
                        size='small'
                        sx={{ width: 300 }}
                        disableClearable
                        loading={statusCompareData === 'loading'}
                        value={outputOptions.find(option => option.id === selectedField?.outputSelected) ?? null}
                        options={outputOptions}
                        groupBy={outputOption => outputOption.nodeName}
                        getOptionLabel={outputOption => outputOption.key}
                        onChange={(_, value) => {
                            setSelectedField(prev => ({
                                ...prev,
                                outputSelected: value?.id ?? ''
                            }))
                        }}
                        autoHighlight
                        renderInput={params => (
                            <TextField
                                {...params}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                label='Choose output to compare...'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {statusCompareData === 'loading' ? <CircularProgress color='inherit' size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                )}
                {selectedTab === 'inputs' && (
                    <Autocomplete
                        id='inputs'
                        size='small'
                        sx={{ width: 300 }}
                        disableClearable
                        loading={statusCompareData === 'loading'}
                        value={inputOptions.find(option => option.id === selectedField?.inputSelected) ?? null}
                        options={inputOptions}
                        groupBy={inputOption => inputOption.nodeName}
                        getOptionLabel={inputOption => inputOption.key}
                        onChange={(_, value) => {
                            setSelectedField(prev => ({
                                ...prev,
                                inputSelected: value?.id ?? ''
                            }))
                        }}
                        autoHighlight
                        renderInput={params => (
                            <TextField
                                {...params}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                label='Choose input to compare...'
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {statusCompareData === 'loading' ? <CircularProgress color='inherit' size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                )}
            </Stack>
            <Divider />
            <Stack sx={{ padding: 2, alignItems: 'center' }} overflow='hidden'>
                {renderContent()}
            </Stack>
        </Paper>
    )
}

export default NodeComparePanel
